import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const NotFoundContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.gradients.card};
  padding: 2rem;
`;

const NotFoundCard = styled(motion.div)`
  background: white;
  border-radius: 30px;
  padding: 3rem;
  text-align: center;
  max-width: 600px;
  width: 100%;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ErrorCode = styled.h1`
  font-size: 8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 6rem;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &.btn-primary {
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    color: white;
    box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
    }
  }
  
  &.btn-outline {
    background: transparent;
    border: 2px solid ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      color: white;
    }
  }
`;

const SuggestedLinks = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  padding: 1.5rem;
  border-radius: 15px;
  margin: 2rem 0;
  
  h3 {
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      margin-bottom: 0.5rem;
      
      a {
        color: ${props => props.theme.colors.primary};
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

const PonyIllustration = styled.div`
  font-size: 4rem;
  margin-bottom: 2rem;
  animation: bounce 2s infinite;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const NotFound = () => {
  return (
    <NotFoundContainer>
      <Helmet>
        <title>Seite nicht gefunden - Der Ponystall</title>
        <meta name="description" content="Die angeforderte Seite wurde nicht gefunden. Kehren Sie zur Startseite zurück oder erkunden Sie unsere Angebote." />
      </Helmet>

      <NotFoundCard
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <PonyIllustration>🐴</PonyIllustration>
        
        <ErrorCode>404</ErrorCode>
        <ErrorTitle>Ups! Seite nicht gefunden</ErrorTitle>
        
        <ErrorMessage>
          Die Seite, die Sie suchen, existiert nicht oder wurde verschoben. 
          Aber keine Sorge - unsere Ponys helfen Ihnen gerne dabei, den richtigen Weg zu finden!
        </ErrorMessage>

        <SuggestedLinks>
          <h3>Beliebte Seiten</h3>
          <ul>
            <li><Link to="/">🏠 Startseite</Link></li>
            <li><Link to="/kurse">🎠 Unsere Kurse</Link></li>
            <li><Link to="/kurse">💳 Online Buchung</Link></li>
            <li><Link to="/ponys">🐴 Unsere Ponys</Link></li>
            <li><Link to="/marcella">👩‍🏫 Über Marcella</Link></li>
          </ul>
        </SuggestedLinks>

        <ActionButtons>
          <ActionButton to="/" className="btn-primary">
            Zur Startseite
          </ActionButton>
          <ActionButton to="/kurse" className="btn-outline">
            Kurse entdecken
          </ActionButton>
        </ActionButtons>
      </NotFoundCard>
    </NotFoundContainer>
  );
};

export default NotFound;